import i18n from "i18next";
import {
	IconAlignCenter,
	IconAlignJustified,
	IconAlignLeft,
	IconAlignRight,
	IconChevronDown,
	IconIndentIncrease,
	IconLetterCase,
	IconList,
	IconListNumbers,
	IconPilcrow,
	IconPlus,
} from "@tabler/icons-react";
import type { Editor } from "@tiptap/core";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { testIds } from "@/testIds";
import { dispatchToolboxInsert } from "@/components/toolbox/insertHandler";
import { paragraphsToList } from "@/lib/tiptap/paragraphsToList";
import { FormatMenu } from "./FormatMenu";
import { InsertMenu } from "./InsertMenu";

// Kept for back-compat with callers/tests that still reference these types.
export type RibbonTab = "home" | "cite" | "review" | "view";
export type ViewMode = "block" | "flow";

interface EditorRibbonProps {
	/** Active tab — retained for back-compat (unused by the quiet ribbon). */
	tab?: RibbonTab;
	onTabChange?: (tab: RibbonTab) => void;
	/** Active TipTap editor — drives mark + node command actions. */
	editor?: Editor | null;
	/** Optional handler for the Block group's delete action. */
	onDeleteBlock?: () => void;
	onAddBlock?: () => void;
	className?: string;
}

/**
 * Flow formatting ribbon — Claude Design "calm mega menu" model (ds27 §6/§7).
 *
 * A single full-width 44px row under the TopBar that is now just three
 * dropdown triggers — every always-on icon row has been folded into the
 * Format / Insert mega menus so the bar reads quiet:
 *
 *   ¶ Body ▾   ·   Aa Text ▾   ·   + Insert ▾
 *
 * Split by SCOPE, not by two words that meant the same thing. "Body" was a
 * value and "Format" was a category, and everything in Body also appeared in
 * Format's Paragraph column — Body was contained by Format. Now ¶ Body owns
 * the current block and Aa Text owns the current selection, with no item in
 * both. Design: lilia-docs/design-handoffs/2026-09-11-editor-chrome/ §8.
 *
 * - Paragraph style ▾ keeps its lightweight portal dropdown (Body / H1–H4 /
 *   Blockquote / Code block).
 * - Format ▾ opens the FormatMenu mega panel (Text · Code & math · Paragraph)
 *   which now houses the inline marks, math, lists, alignment, color, line
 *   spacing and indentation that used to live on the bar.
 * - Insert ▾ opens the InsertMenu mega panel (unchanged).
 *
 * Both megas portal to <body> (createPortal), so the ribbon's overflow-x clip
 * can't cut them off. Command implementations are reused verbatim from the
 * previous ribbon / EditorContext — only layout / grouping / testids changed.
 */
export function EditorRibbon(props: EditorRibbonProps) {
	const { editor, className } = props;
	return (
		<div
			data-testid="format-toolbar"
			// Keep the legacy testids too — existing e2e selects on these.
			data-legacy-testid="editor-ribbon editor-toolbar"
			role="toolbar"
			aria-label={i18n.t("editor:ui.formatting")}
			className={cn(
				// Default: full bar. Flow trial 2026-07-18 also mounts with
				// variant classes (borderless floating chip) from EditorPage.
				"flex items-center gap-1 h-[44px] px-2 bg-[var(--lilia-surface)] border-b border-[var(--lilia-border)] shrink-0 overflow-x-auto",
				className,
			)}
		>
			{/* Legacy testid anchors — zero-size, so old specs that look up
			    `editor-ribbon` / `editor-toolbar` still resolve. */}
			<span data-testid="editor-ribbon" className="sr-only" aria-hidden="true" />
			<span data-testid="editor-toolbar" className="sr-only" aria-hidden="true" />

			<ParagraphStyleMenu editor={editor} />
			<Divider />
			<FormatRibbonButton editor={editor} />
			<Divider />
			<InsertRibbonButton />
		</div>
	);
}

// ──────────────────────────────────────────────────────────────────────
// 1 · Paragraph style ▾
// ──────────────────────────────────────────────────────────────────────

interface ParagraphStyleItem {
	id: string;
	testId: string;
	label: string;
	isActive: (editor: Editor) => boolean;
	apply: (editor: Editor) => void;
}

/** Groups inside `¶ Body`. The first group names the block, so it is the one
 *  the trigger label reads from; the rest act on the same block without
 *  changing what it *is*. */
interface BlockGroup {
	key: string;
	label: string;
	items: ParagraphStyleItem[];
}

const BLOCK_GROUPS: BlockGroup[] = [
	{
		key: "type",
		label: i18n.t("editor:ribbonStyles.blockType"),
		items: [
			{
				id: "body",
				testId: "heading-body",
				label: i18n.t("editor:ribbonStyles.body"),
				isActive: (e) => e.isActive("paragraph"),
				apply: (e) => e.chain().focus().setParagraph().run(),
			},
			...[1, 2, 3, 4].map((level) => ({
				id: `heading-${level}`,
				testId: `heading-${level}`,
				label: i18n.t("editor:ribbonStyles.heading", { level }),
				isActive: (e: Editor) =>
					e.isActive("heading") && e.getAttributes("heading").level === level,
				apply: (e: Editor) =>
					e
						.chain()
						.focus()
						.toggleHeading({ level: level as 1 | 2 | 3 | 4 })
						.run(),
			})),
			{
				id: "blockquote",
				testId: "heading-blockquote",
				label: "Blockquote",
				isActive: (e) => e.isActive("blockquote"),
				// Flow's blockquote is a custom block node (StarterKit blockquote is
				// disabled) — route through the shared toolbox insert dispatch so the
				// behaviour matches the Insert menu / slash command exactly.
				apply: () => dispatchToolboxInsert({ kind: "blockquote" }, "toolbox"),
			},
			{
				id: "code-block",
				testId: "heading-code-block",
				label: "Code block",
				isActive: (e) => e.isActive("code") || e.isActive("codeBlock"),
				// Code block is likewise a custom block node in Flow.
				apply: () => dispatchToolboxInsert({ kind: "code" }, "toolbox"),
			},
		],
	},
	{
		key: "lists",
		label: "Lists",
		items: [
			{
				id: "list-bullet",
				testId: "list-bullet",
				label: "Bullet list",
				isActive: (e) => e.isActive("listBlock", { ordered: false }),
				apply: (e) => paragraphsToList(e, false),
			},
			{
				id: "list-numbered",
				testId: "list-numbered",
				label: "Numbered list",
				isActive: (e) => e.isActive("listBlock", { ordered: true }),
				apply: (e) => paragraphsToList(e, true),
			},
		],
	},
	{
		key: "align",
		label: "Alignment",
		items: [
			{
				id: "align-left",
				testId: "align-left",
				label: "Align left",
				isActive: (e) => e.isActive({ textAlign: "left" }),
				apply: (e) => e.chain().focus().setTextAlign("left").run(),
			},
			{
				id: "align-center",
				testId: "align-center",
				label: "Align center",
				isActive: (e) => e.isActive({ textAlign: "center" }),
				apply: (e) => e.chain().focus().setTextAlign("center").run(),
			},
			{
				id: "align-right",
				testId: "align-right",
				label: "Align right",
				isActive: (e) => e.isActive({ textAlign: "right" }),
				apply: (e) => e.chain().focus().setTextAlign("right").run(),
			},
			{
				id: "align-justify",
				testId: "align-justify",
				label: "Justify",
				isActive: (e) => e.isActive({ textAlign: "justify" }),
				apply: (e) => e.chain().focus().setTextAlign("justify").run(),
			},
		],
	},
	{
		key: "indent",
		label: "Indentation",
		items: [
			{
				id: "format-indent",
				testId: "format-indent",
				label: "Indent",
				isActive: () => false,
				// Only where the schema has nested list items (the per-block editors); the document
				// editor's lists are blocks, indented inside the list itself.
				apply: (e) => (e.schema.nodes.listItem ? e.chain().focus().sinkListItem("listItem").run() : false),
			},
		],
	},
];

/** Icons, by item id — kept out of the data so the groups stay readable. */
const BLOCK_ICONS: Record<string, typeof IconPilcrow> = {
	"list-bullet": IconList,
	"list-numbered": IconListNumbers,
	"align-left": IconAlignLeft,
	"align-center": IconAlignCenter,
	"align-right": IconAlignRight,
	"align-justify": IconAlignJustified,
	"format-indent": IconIndentIncrease,
};

function currentParagraphLabel(editor: Editor | null | undefined): string {
	if (!editor) return i18n.t("editor:ribbonStyles.body");
	const match = BLOCK_GROUPS[0].items.find((s) => s.isActive(editor));
	return match?.label ?? i18n.t("editor:ribbonStyles.body");
}

function ParagraphStyleMenu({ editor }: { editor?: Editor | null }) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

	useEffect(() => {
		if (!open) {
			setPos(null);
			return;
		}
		const r = triggerRef.current?.getBoundingClientRect();
		if (r) setPos({ left: r.left, top: r.bottom + 4 });
	}, [open]);

	useEffect(() => {
		if (!open) return;
		function onDocClick(e: MouseEvent) {
			const t = e.target as Node;
			if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
			setOpen(false);
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", onDocClick);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDocClick);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	return (
		<div className="shrink-0">
			<button
				ref={triggerRef}
				type="button"
				data-testid={testIds.editor.fmtBlock}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				disabled={!editor}
				className={cn(
					"inline-flex items-center gap-1.5 h-[var(--lilia-control-h,30px)] px-2.5 rounded-[8px] text-[12px] font-medium transition-colors",
					"text-[var(--lilia-text)] hover:bg-[var(--lilia-hover)]",
					"disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
				)}
				title={i18n.t("editor:ui.blockHint")}
			>
				<IconPilcrow size={16} className="text-[var(--lilia-text-muted)]" />
				<span className="min-w-[64px] text-left">{currentParagraphLabel(editor)}</span>
				<IconChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
			</button>
			{open &&
				pos &&
				createPortal(
					<div
						ref={menuRef}
						role="menu"
						data-testid="heading-menu"
						aria-label={i18n.t("editor:ui.block")}
						className="fixed z-[1000] min-w-[176px] bg-[var(--lilia-surface)] border border-[var(--lilia-border)] rounded-md shadow-[var(--lilia-shadow-lg)] p-1"
						style={{ left: pos.left, top: pos.top }}
					>
						{BLOCK_GROUPS.map((group, gi) => (
							<div key={group.key} role="group" aria-label={group.label}>
								{gi > 0 && (
									<div
										aria-hidden="true"
										className="my-1 h-px bg-[var(--lilia-border)]"
									/>
								)}
								{group.items.map((item) => {
									const active = editor ? item.isActive(editor) : false;
									const Icon = BLOCK_ICONS[item.id];
									return (
										<button
											key={item.id}
											type="button"
											role="menuitemradio"
											aria-checked={active}
											data-testid={item.testId}
											onClick={() => {
												if (editor) item.apply(editor);
												setOpen(false);
											}}
											className={cn(
												"flex w-full items-center justify-between gap-3 rounded px-2.5 h-8 text-[13px] text-left transition-colors",
												"hover:bg-[var(--lilia-hover)]",
												active ? "text-[var(--lilia-primary)] font-medium" : "text-[var(--lilia-text)]",
											)}
										>
											<span className="inline-flex items-center gap-2">
												{Icon && (
													<Icon size={15} className="text-[var(--lilia-text-muted)] shrink-0" />
												)}
												{item.label}
											</span>
											{active && (
												<span
													aria-hidden="true"
													className="h-1.5 w-1.5 rounded-full bg-[var(--lilia-primary)]"
												/>
											)}
										</button>
									);
								})}
							</div>
						))}
					</div>,
					document.body,
				)}
		</div>
	);
}

// ──────────────────────────────────────────────────────────────────────
// 2 · Format ▾  (opens the FormatMenu mega panel)
// ──────────────────────────────────────────────────────────────────────

/**
 * Aa Text ▾ — the current SELECTION: inline marks, code & math, colour.
 * Primary-tinted, same affordance as Insert ▾. Block-level items (headings,
 * lists, alignment, indentation) deliberately do not live here — they are in
 * ¶ Body, and nothing appears in both.
 */
function FormatRibbonButton({ editor }: { editor?: Editor | null }) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	return (
		<div className="shrink-0">
			<button
				ref={triggerRef}
				type="button"
				data-testid={testIds.editor.fmtText}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				className={cn(
					"inline-flex items-center gap-1 h-[var(--lilia-control-h,30px)] px-2.5 rounded-[8px] text-[12px] font-medium transition-colors",
					open
						? "bg-[var(--lilia-primary-subtle)] text-[var(--lilia-primary)]"
						: "text-[var(--lilia-primary)] hover:bg-[var(--lilia-primary-subtle)]",
				)}
			>
				<IconLetterCase size={16} />
				<span>{i18n.t("editor:ui.text")}</span>
				<IconChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
			</button>
			<FormatMenu
				open={open}
				onClose={() => setOpen(false)}
				editor={editor}
				anchorEl={triggerRef.current}
			/>
		</div>
	);
}

// ──────────────────────────────────────────────────────────────────────
// 3 · Insert ▾  (ALREADY BUILT — kept verbatim, only re-housed in order)
// ──────────────────────────────────────────────────────────────────────

/**
 * Insert ▾ — opens the Insert mega menu (Surface 2). The primary-tinted
 * entry into the block library; the menu reads the toolbox registry so it
 * stays in lockstep with the Toolbox / slash insert.
 */
function InsertRibbonButton() {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	// ⌘/ (and other insert entry points) open the mega menu via this event —
	// it replaced the old left Toolbox panel.
	useEffect(() => {
		const onOpen = () => setOpen(true);
		window.addEventListener("lilia:open-insert-menu", onOpen);
		return () => window.removeEventListener("lilia:open-insert-menu", onOpen);
	}, []);
	return (
		<div className="shrink-0">
			<button
				ref={triggerRef}
				type="button"
				data-testid={testIds.editor.fmtInsert}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				className={cn(
					"inline-flex items-center gap-1 h-[var(--lilia-control-h,30px)] px-2.5 rounded-[8px] text-[12px] font-medium transition-colors",
					open
						? "bg-[var(--lilia-primary-subtle)] text-[var(--lilia-primary)]"
						: "text-[var(--lilia-primary)] hover:bg-[var(--lilia-primary-subtle)]",
				)}
			>
				<IconPlus size={16} />
				<span>{i18n.t("editor:ui.insert")}</span>
				<IconChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
			</button>
			<InsertMenu open={open} onClose={() => setOpen(false)} anchorEl={triggerRef.current} />
		</div>
	);
}

// ──────────────────────────────────────────────────────────────────────
// Shared primitives
// ──────────────────────────────────────────────────────────────────────

function Divider() {
	return <div aria-hidden="true" className="self-center mx-0.5 w-px h-5 bg-[var(--lilia-border)] shrink-0" />;
}
